import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Users, Loader2, Wifi, WifiOff, Star, Search, CheckCircle2, XCircle, IndianRupee, Package, ShieldCheck, ShieldOff, Download } from "lucide-react";
import { toast } from "sonner";

export default function Agents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionId, setActionId] = useState(null);

  const fetchAgents = async (q) => {
    setLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}` : "";
      const { data } = await api.get(`/admin/agents/detailed${params}`);
      setAgents(data);
    } catch {
      try {
        const { data } = await api.get("/agents");
        setAgents(data);
      } catch (err) { console.error("Agents: fallback /agents fetch failed", err); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleSearch = (e) => { e.preventDefault(); fetchAgents(search); };

  const handleApprove = async (agentId, approve) => {
    setActionId(agentId);
    try {
      await api.put(`/admin/agents/${agentId}/approve`, { is_verified: approve });
      toast.success(approve ? "Agent approved!" : "Agent suspended");
      fetchAgents(search);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setActionId(null); }
  };

  const handleToggleOnline = async (agentId, online) => {
    setActionId(agentId);
    try {
      await api.put(`/admin/agents/${agentId}/toggle-status`, { is_online: online });
      toast.success(online ? "Agent set online" : "Agent set offline");
      fetchAgents(search);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setActionId(null); }
  };

  const handleExportAgents = () => {
    const token = localStorage.getItem("token");
    const base = api.defaults.baseURL || "";
    const url = `${base}/admin/export/agents-csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = "pickngo_agents.csv";
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="agents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="agents-heading">Agent Management</h1>
          <p className="text-sm text-slate-500 mt-1">View, approve, and manage delivery agents</p>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search agents..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-xl border-slate-200 w-56" data-testid="agent-search" />
            </div>
            <Button type="submit" className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl">Search</Button>
          </form>
          <Button variant="outline" onClick={handleExportAgents} className="rounded-xl border-slate-200 text-sm" data-testid="export-agents-csv">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 text-center">
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{agents.length}</p>
          <p className="text-xs text-slate-500">Total Agents</p>
        </div>
        <div className="bg-green-50 rounded-2xl border border-green-100 p-4 text-center">
          <p className="font-['Outfit'] text-2xl font-bold text-green-700">{agents.filter(a => a.is_online).length}</p>
          <p className="text-xs text-green-600">Online</p>
        </div>
        <div className="bg-blue-50 rounded-2xl border border-blue-100 p-4 text-center">
          <p className="font-['Outfit'] text-2xl font-bold text-blue-700">{agents.filter(a => a.is_verified).length}</p>
          <p className="text-xs text-blue-600">Verified</p>
        </div>
        <div className="bg-amber-50 rounded-2xl border border-amber-100 p-4 text-center">
          <p className="font-['Outfit'] text-2xl font-bold text-amber-700">{agents.filter(a => !a.is_verified).length}</p>
          <p className="text-xs text-amber-600">Pending Approval</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="agents-table">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00] mx-auto" /></div>
        ) : agents.length === 0 ? (
          <div className="p-12 text-center" data-testid="no-agents">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No agents found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/40">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6">Agent</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Rating</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Deliveries</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Earnings</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Verified</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow
                    key={agent._id}
                    className="border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/agents/${agent._id}`)}
                    data-testid={`agent-row-${agent._id}`}
                  >
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="text-[#FF6B00] text-sm font-semibold font-['Outfit']">{(agent.name || "A")[0].toUpperCase()}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-slate-900 block">{agent.name}</span>
                          <span className="text-[11px] text-slate-400">{agent.email}</span>
                          {agent.phone && <span className="text-[11px] text-slate-400 block">{agent.phone}</span>}
                          {agent.address && <span className="text-[11px] text-slate-400 block">{agent.address}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {agent.is_online ? (
                        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-medium"><Wifi className="h-3 w-3" /> Online</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 px-2.5 py-0.5 rounded-full text-xs font-medium"><WifiOff className="h-3 w-3" /> Offline</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Star className={`h-3.5 w-3.5 ${agent.avg_rating > 0 ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                        <span className="text-sm font-medium text-slate-700">{agent.avg_rating > 0 ? agent.avg_rating : "N/A"}</span>
                        {agent.total_ratings > 0 && <span className="text-[10px] text-slate-400">({agent.total_ratings})</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700">
                        <Package className="h-3 w-3 text-slate-400" />
                        {agent.completed_deliveries ?? 0}
                        {agent.active_orders > 0 && <span className="text-[10px] text-blue-500 ml-1">+{agent.active_orders} active</span>}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-semibold text-slate-900 flex items-center gap-0.5">
                        <IndianRupee className="h-3.5 w-3.5" />{agent.total_earnings ?? 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {agent.is_verified ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Verified</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><XCircle className="h-3.5 w-3.5" /> Pending</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        {agent.is_verified ? (
                          <Button size="sm" variant="outline" onClick={() => handleApprove(agent._id, false)} disabled={actionId === agent._id} className="rounded-lg px-2 py-1 text-[10px] h-7 border-red-200 text-red-600 hover:bg-red-50" data-testid={`agent-suspend-${agent._id}`}>
                            <ShieldOff className="h-3 w-3 mr-0.5" /> Suspend
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => handleApprove(agent._id, true)} disabled={actionId === agent._id} className="rounded-lg px-2 py-1 text-[10px] h-7 bg-green-600 text-white hover:bg-green-700" data-testid={`agent-approve-${agent._id}`}>
                            <ShieldCheck className="h-3 w-3 mr-0.5" /> Approve
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleToggleOnline(agent._id, !agent.is_online)} disabled={actionId === agent._id} className="rounded-lg px-2 py-1 text-[10px] h-7" data-testid={`agent-toggle-${agent._id}`}>
                          {agent.is_online ? <WifiOff className="h-3 w-3 mr-0.5" /> : <Wifi className="h-3 w-3 mr-0.5" />}
                          {agent.is_online ? "Offline" : "Online"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
